import {ReactNode} from "react"
import {
    Alert,
    Badge,
    Button,
    Card,
    Col,
    Form,
    ProgressBar,
    Row,
} from "react-bootstrap"

import JsonEditor from "./JsonEditor"
import type {SeatPlan, Survey, SurveyTask} from "./types"

export default function RoomSurveyComponent({
    survey,
    seatPlan,
    editing,
    savingKey,
    onGenerate,
    onUpdateTask,
    onSaveTask,
    onSaveSettings,
    renderSketch,
}: {
    survey: Survey | null
    seatPlan: SeatPlan | null
    editing: boolean
    savingKey: string | null
    onGenerate: () => void
    onUpdateTask: (
        taskKey: string,
        updater: (task: SurveyTask) => SurveyTask
    ) => void
    onSaveTask: (task: SurveyTask) => void
    onSaveSettings: (survey: Survey) => Promise<void>
    renderSketch: (task: SurveyTask) => ReactNode
}) {
    if (!survey) {
        return <Alert variant="info">
            Für diesen Hörsaal wurden noch keine Messaufgaben erzeugt.
            {editing && <Button size="sm" className="ms-3"
                onClick={onGenerate} disabled={savingKey === "GENERATE"}>
                Mess-To-do-Liste erzeugen
            </Button>}
        </Alert>
    }

    const relevant = survey.tasks.filter(
        task => task.status !== "NOT_REQUIRED"
    )
    const completed = relevant.filter(task => task.status === "DONE").length
    const percent = relevant.length ? completed / relevant.length * 100 : 0

    return <>
        <Card className="mb-3">
            <Card.Body>
                <div className="d-flex justify-content-between
                    align-items-center mb-2">
                    <strong>Vermessungsfortschritt</strong>
                    <span>{completed}/{relevant.length}</span>
                </div>
                <ProgressBar now={percent}
                    variant={percent === 100 ? "success" : "primary"}/>
                {editing && <Button size="sm" variant="outline-secondary"
                    className="mt-3" onClick={onGenerate}
                    disabled={savingKey === "GENERATE"}>
                    Aufgaben aus aktuellem Plan aktualisieren
                </Button>}
            </Card.Body>
        </Card>
        <div className="d-grid gap-3">
            {survey.tasks.map(task =>
                <Card key={task.key}
                    border={task.status === "DONE" ? "success"
                        : task.status === "DEVIATION" ? "danger" : undefined}>
                    <Card.Header className="d-flex justify-content-between
                        align-items-center">
                        <strong>{task.title}</strong>
                        <Badge bg={task.status === "DONE" ? "success"
                            : task.status === "DEVIATION" ? "danger" : "warning"}
                            text={task.status === "OPEN" ? "dark" : undefined}>
                            {task.status === "DONE" ? "erledigt"
                                : task.status === "DEVIATION"
                                    ? "Abweichung" : "offen"}
                        </Badge>
                    </Card.Header>
                    <Card.Body>
                        <p>{task.instruction}</p>
                        {seatPlan && renderSketch(task)}
                        <Row className="g-3">
                            {task.inputs.map(input =>
                                <Col md={task.inputs.length > 2 ? 3 : 6}
                                    key={input.key}>
                                    <Form.Label htmlFor={`${task.key}-${input.key}`}>
                                        {input.label}
                                    </Form.Label>
                                    <div className="input-group">
                                        <Form.Control
                                            id={`${task.key}-${input.key}`}
                                            type={input.inputType}
                                            step={input.inputType === "number"
                                                ? .01 : undefined}
                                            value={input.value ?? ""}
                                            disabled={!editing}
                                            onChange={event =>
                                                onUpdateTask(task.key,
                                                    current => ({
                                                        ...current,
                                                        inputs: current.inputs.map(
                                                            item => item.key ===
                                                                input.key
                                                                ? {...item, value:
                                                                    event.target.value}
                                                                : item
                                                        ),
                                                    }))}
                                        />
                                        {input.unit && <span
                                            className="input-group-text">
                                            {input.unit}
                                        </span>}
                                    </div>
                                </Col>)}
                        </Row>
                        <Form.Label className="mt-3"
                            htmlFor={`${task.key}-note`}>Notiz</Form.Label>
                        <Form.Control
                            id={`${task.key}-note`}
                            value={task.note ?? ""}
                            disabled={!editing}
                            onChange={event => onUpdateTask(task.key,
                                current => ({
                                    ...current,
                                    note: event.target.value,
                                }))}
                        />
                        {editing && <div className="d-flex gap-2 mt-3">
                            <Button size="sm" onClick={() => onSaveTask(task)}
                                disabled={savingKey === task.key}>
                                Messung speichern
                            </Button>
                            <Button size="sm" variant="outline-danger"
                                onClick={() => onSaveTask({
                                    ...task,
                                    status: "DEVIATION",
                                })}>
                                Abweichung melden
                            </Button>
                        </div>}
                    </Card.Body>
                </Card>)}
        </div>
        <Card className="mt-3">
            <Card.Header>
                <strong>Koordinatensystem und Gänge</strong>
            </Card.Header>
            <Card.Body>
                <JsonEditor title="Vermessungsgrunddaten"
                    value={survey} onSave={onSaveSettings}
                    readOnly={!editing}/>
            </Card.Body>
        </Card>
    </>
}
